/* eslint-disable default-case */
import React, { useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi } from "ag-grid-community";
import { useFormikContext } from "formik";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  Box,
  ButtonCancelSecondary,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../../components/utils";
import { TransactionDetailsData } from "../../../types/Transaction";
import FilterOptions from "./utils/FilterOptions";
import Avatar from "../../esopOverview/Avatar";
import GenericTableHeader from "../../../shared/GenericTableHeader";
import SearchInput from "../../../components/shared/SearchInput";
import { MonetaryEventState } from "../../../types/OptionBuyback";
import { useDeleteTransaction } from "../../../queries/transactionV2";
import { saveTransactionDetails } from "../../../api/TransactionApisV2";
import { getCurrencyType } from "../../../utils/currencyFormatter";

interface PreviewDetailsProps {
  onStepChange: (e?: number) => void;
  onBackClick: (e?: number) => void;
}

function EmployeeInfoRender(emp: any) {
  return (
    <HStack>
      <Avatar name={emp.data.employeeName} status={emp.data.employmentStatus} />
      <Box className="ml-2">
        <p className="text-xs font-medium text-gray-dark">
          {emp.data.employeeName}
        </p>
        <p className="capitalize text-xxs text-gray-light">
          {emp.data.employeeDepartment || ""} |{" "}
          {emp.data.employeeDesignation || ""}
        </p>
      </Box>
    </HStack>
  );
}

const PersonnelListWithFilterDetails = (props: PreviewDetailsProps) => {
  const gridApi = useRef<GridApi | any>(null);
  const currency = getCurrencyType();
  const onGridReady = (params: any) => {
    gridApi.current = params;
  };

  // Filter states
  const formik = useFormikContext<TransactionDetailsData>();

  const rowData = useMemo(() => formik.values.previewList || [], []);

  const filteredRowData = useMemo(
    () =>
      rowData.filter((employee) => {
        if (
          formik.values.offerFilter.selectedEmployeeIds &&
          formik.values.offerFilter.selectedEmployeeIds.length > 0 &&
          formik.values.offerFilter.selectedEmployeeIds
            .map((e: any) => e.id || e.employeeId)
            .includes(employee.employeeId)
        ) {
          return true;
        }
        // Department filter
        if (
          formik.values.offerFilter.department &&
          formik.values.offerFilter.department.length > 0 &&
          !formik.values.offerFilter.department.includes(
            employee.employeeDepartment
          )
        ) {
          return false;
        }

        // Min vested options filter
        if (
          formik.values.offerFilter.vestedOptions &&
          formik.values.offerFilter.vestedOptions.min &&
          (employee.totalVestedOptions || 0) <
            formik.values.offerFilter.vestedOptions.min
        ) {
          return false;
        }
        // Max vested options filter
        if (
          formik.values.offerFilter.vestedOptions &&
          formik.values.offerFilter.vestedOptions.max &&
          (employee.totalVestedOptions || 0) >
            formik.values.offerFilter.vestedOptions.max
        ) {
          return false;
        }

        if (
          formik.values.offerFilter.eligibleOptions &&
          formik.values.offerFilter.eligibleOptions.min &&
          (employee.totalEligibleOptions || 0) <
            formik.values.offerFilter.eligibleOptions.min
        ) {
          return false;
        }
        // Max vested options filter
        if (
          formik.values.offerFilter.eligibleOptions &&
          formik.values.offerFilter.eligibleOptions.max &&
          (employee.totalEligibleOptions || 0) >
            formik.values.offerFilter.eligibleOptions.max
        ) {
          return false;
        }

        if (
          formik.values.offerFilter.employmentType &&
          formik.values.offerFilter.employmentType.length > 0
        ) {
          const employmentTypes = employee.employmentType || [];
          if (
            !formik.values.offerFilter.employmentType.some((type: any) =>
              employmentTypes.includes(type)
            )
          ) {
            return false;
          }
        }

        if (
          formik.values.offerFilter.tenure &&
          ((formik.values.offerFilter.tenure.min || 0) > 0 ||
            (formik.values.offerFilter.tenure.max || Infinity) < Infinity)
        ) {
          if (
            formik.values.offerFilter.tenure.min &&
            employee.tenure < formik.values.offerFilter.tenure.min
          ) {
            return false;
          }
          if (
            formik.values.offerFilter.tenure.max &&
            employee.tenure > formik.values.offerFilter.tenure.max
          ) {
            return false;
          }
        }

        if (
          formik.values.offerFilter.status &&
          formik.values.offerFilter.status.length > 0
        ) {
          const status = employee.employeeState || "";
          if (!formik.values.offerFilter.status.includes(status)) {
            return false;
          }
        }

        if (formik.values.offerFilter.planIds) {
          const filteredGrants =
            formik.values.offerFilter.planIds.length > 0
              ? employee.grantRelatedDetails.filter((grant) =>
                  formik.values.offerFilter.planIds
                    ?.map((p) => p.id)
                    .includes(grant.planId)
                )
              : employee.grantRelatedDetails;

          employee.totalVestedOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.vestedOptions || 0),
            0
          );
          employee.totalEligibleOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.eligibleOptions || 0),
            0
          );

          employee.totalGrantedOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.grantedOptions || 0),
            0
          );
          employee.totalCashExercisedOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.cashExercisedOptions || 0),
            0
          );
          employee.totalStockExercisedOptions = filteredGrants.reduce(
            (sum, grant) => sum + (grant.stockExercisedOptions || 0),
            0
          );
        }

        if (
          formik.values.offerFilter.companyIds &&
          formik.values.offerFilter.companyIds.length > 0
        ) {
          const filterCompanyIds = formik.values.offerFilter.companyIds.map(
            (c: any) => c.id || c
          );

          if (
            filterCompanyIds.includes(employee.companyId) &&
            !employee.subsidiaryCompanyId
          ) {
            return true;
          } else if (filterCompanyIds.includes(employee.subsidiaryCompanyId)) {
            return true;
          } else return false;
        }

        if (
          formik.values.offerFilter.excludeEmployeeIds &&
          formik.values.offerFilter.excludeEmployeeIds.length > 0
        ) {
          return !formik.values.offerFilter.excludeEmployeeIds
            .map((e) => e.id)
            .includes(employee.employeeId);
        }

        if (
          formik.values.offerFilter.participationConstraints &&
          formik.values.offerFilter.participationConstraints.length > 0
        ) {
          const constraints =
            formik.values.offerFilter.participationConstraints;
          const participatedDates = employee.participatedEventDates || [];

          if (
            !constraints ||
            constraints.length === 0 ||
            !participatedDates ||
            participatedDates.length === 0
          ) {
            return true;
          }

          for (const constraint of constraints) {
            const { interval, intervalUnit, maxFrequency } = constraint;

            const now = new Date();
            const startDate = new Date();

            switch (intervalUnit) {
              case "Days":
                startDate.setDate(now.getDate() - interval);
                break;
              case "Month":
                startDate.setMonth(now.getMonth() - interval);
                break;
              case "Quarter":
                startDate.setMonth(now.getMonth() - interval * 3);
                break;
              case "Year":
                startDate.setFullYear(now.getFullYear() - interval);
                break;
            }

            const participationCount = employee.participatedEventDates.filter(
              (date) => new Date(date) >= startDate && new Date(date) <= now
            ).length;

            if (participationCount >= maxFrequency) {
              return false;
            }
          }
          return true;
        }

        if (
          formik.values.offerFilter.nationality &&
          formik.values.offerFilter.nationality.length > 0 &&
          !formik.values.offerFilter.nationality.includes(
            employee.nationality || ""
          )
        ) {
          return false;
        }

        return true;
      }),

    [rowData, formik.values.offerFilter]
  );

  const columnDefs: ColDef[] = [
    {
      headerName: "EMPLOYEE NAME",
      width: 200,
      autoHeight: true,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },
      wrapText: true,
      colId: "employeeName",
      valueGetter: (params: any) => ({
        employeeName: params.data?.employeeName,
        employeeDepartment: params.data?.employeeDepartment,
        employeeDesignation: params.data?.employeeDesignation,
        hrId: params.data?.hrId,
      }),
      filter: "agMultiColumnFilter",
      menuTabs: ["filterMenuTab"],
      filterParams: {
        maxNumConditions: 5,
        buttons: ["reset"],
        filters: [
          {
            title: "Name",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeName,
              valueFormatter: (params: any) => params.value?.employeeName,
            },
          },
          {
            title: "Department",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeDepartment,
              valueFormatter: (params: any) => params.value?.employeeDepartment,
            },
          },
          {
            title: "Designation",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeDesignation,
              valueFormatter: (params: any) =>
                params.value?.employeeDesignation,
            },
          },
          {
            title: "Emp ID",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.hrId,
              valueFormatter: (params: any) => params.value?.hrId,
            },
          },
        ],
      },
      initialWidth: 200,
      minWidth: 200,
      cellRendererParams: (params: any) => ({
        employeeName: params.data.employeeName,
        employeeDesignation: params.data.employeeDesignation,
        employeeDepartment: params.data.employeeDepartment,
        employeeState: params.data.employeeState,
      }),
      comparator(valueA, valueB) {
        return valueA?.employeeName
          ?.toLowerCase()
          ?.localeCompare(valueB?.employeeName?.toLowerCase());
      },
      cellRenderer: (params: any) =>
        params.data.employeeName === "Total"
          ? "Total"
          : EmployeeInfoRender(params),
      getQuickFilterText: (params: any) =>
        [
          params.data.employeeName,
          params.data.employeeDesignation,
          params.data.employeeDepartment,
          params.data.employeeState,
        ]
          .filter(Boolean)
          .join(" "),
    },
    {
      headerName: "EMP ID",
      autoHeight: true,
      field: "hrId",
      flex: 1,
      width: 80,
    },
    {
      headerName: "VESTED OPTIONS",
      autoHeight: true,
      field: "totalVestedOptions",
      flex: 1,
      width: 80,
      valueFormatter: (params: any) => {
        const vestedOptions = params.value?.toLocaleString(currency);
        return vestedOptions;
      },
    },
    {
      headerName: "ELIGIBLITY",
      autoHeight: true,

      valueGetter: (params) => {
        const percentage =
          formik.values.monetaryDetails.percentageOfNetVestedOptions || 0;
        const totalEligibleOptions = params.data.totalEligibleOptions || 0;

        return percentage > 0
          ? Math.floor(totalEligibleOptions * (percentage / 100))
          : totalEligibleOptions;
      },
      flex: 1,
      width: 80,
      valueFormatter: (params: any) => {
        const eligibility = params.value?.toLocaleString(currency);
        return eligibility;
      },
    },
    {
      headerName: "STATUS",
      autoHeight: true,
      field: "employeeState",
      flex: 1,
      width: 80,
    },
    {
      headerName: "NATIONALITY",
      autoHeight: true,
      field: "nationality",
      flex: 1,
      width: 100,
      valueFormatter: (params: any) => params.value || "-",
    },
  ];

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
      filterParams: {
        buttons: [],
      },
      menuTabs: [],
    }),
    []
  );

  const handleBackClick = () => {
    if (formik.values.monetaryDetails?.considerBlackOutDays) {
      props.onBackClick(3);
    } else {
      props.onBackClick(2);
    }
  };

  const handlePreviewClick = () => {
    if (!formik.values.id) {
      saveTransactionDetails(formik.values);
    }
    props.onStepChange();
  };

  const { mutate: deleteTransaction } = useDeleteTransaction();
  const navigate = useNavigate();
  const handleDiscard = () => {
    if (
      formik.values.id &&
      formik.values.eventState === MonetaryEventState.DRAFT
    ) {
      deleteTransaction(formik.values.id, {
        onSuccess: () => {
          toast.success("Draft Exercise Discarded Successfully");
          navigate("/options/esopTransactions");
        },
        onError: (error: any) => {
          toast(error.response.data.errorRaw, {
            type: "error",
            autoClose: 5000,
          });
        },
      });
    } else {
      navigate("/options/esopTransactions");
    }
  };

  function setPinnedBottomRowData({ api }: { api: any }) {
    const data = api.rowModel.rootNode;
    const filteredData = data.childrenAfterAggFilter.map(
      (element: any) => element.data
    );

    const totalVestedOptions = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.totalVestedOptions,
      0
    );
    const totalEligibility = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.totalEligibleOptions,
      0
    );

    api.setPinnedBottomRowData([
      {
        employeeName: "Total",
        totalVestedOptions,
        totalEligibleOptions: totalEligibility,
        isTotalRow: true,
      },
    ]);
  }

  return (
    <VStack className="w-full">
      <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between text-xl font-semibold text-black-501">
          Participant List
        </HStack>
        <HStack className="flex text-sm font-medium text-gray-500">
          Your filtered list of participants: View of all employees who qualify
          for this exercise event based on the rules and filters you configured
          during setup. Use this to track and manage participation effectively.
        </HStack>
      </VStack>

      <VStack className="w-full px-6 py-10 border-b">
        <HStack className="flex flex-wrap min-w-full gap-4 ">
          <FilterOptions
            formik={formik}
            disableUpdate={
              formik.values.eventState !== MonetaryEventState.DRAFT
            }
          />
        </HStack>
      </VStack>
      <HStack className="justify-between px-6 pt-4">
        <GenericTableHeader
          heading={"Employees"}
          subHeading={""}
          customClassName="flex flex-col px-0 lg:justify-start"
          additionalInfo={<>{`${filteredRowData?.length} employees`}</>}
        ></GenericTableHeader>
        <HStack className="items-center gap-4 ">
          <SearchInput
            className=""
            placeholder="Search"
            onChange={(e: any) => {
              gridApi.current.api.setQuickFilter(e.target.value);
            }}
          />
        </HStack>
      </HStack>
      {/* AG Grid table */}
      <HStack className="justify-between w-full pt-6 pb-6 overflow-auto">
        <Box
          style={{
            height: `${
              rowData.length <= 3
                ? "300"
                : rowData.length >= 10
                ? "600"
                : rowData.length * 100
            }px`,
          }}
          className="w-full h-full max-h-full overflow-auto bg-black ag-theme-material"
        >
          <AgGridReact
            getRowStyle={(params) => {
              if (params.data?.isTotalRow) {
                return {
                  fontWeight: 600,
                };
              }
              return undefined;
            }}
            onRowDataUpdated={setPinnedBottomRowData}
            rowClass={
              "border-t border-dashed cursor-pointer hover:bg-slate-50 "
            }
            alwaysMultiSort
            onGridReady={onGridReady}
            animateRows={true}
            defaultColDef={defaultColDef}
            rowData={filteredRowData}
            suppressCopyRowsToClipboard={true}
            suppressCopySingleCellRanges={true}
            suppressCellFocus={true}
            suppressMenuHide={true}
            columnDefs={columnDefs}
            suppressRowTransform={true}
            rowMultiSelectWithClick={true}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
            }
          ></AgGridReact>
        </Box>
      </HStack>

      {/* Bottom navigation */}
      <HStack className="justify-between w-full p-6">
        <ButtonPrimary1 onClick={handleBackClick}>Back</ButtonPrimary1>
        <HStack className="gap-2">
          {formik.values.eventState === MonetaryEventState.DRAFT && (
            <ButtonPrimary1 className="" onClick={handleDiscard}>
              Discard
            </ButtonPrimary1>
          )}
          <ButtonPrimary
            className="text-white bg-orange-500 hover:bg-orange-600"
            onClick={handlePreviewClick}
          >
            Preview
          </ButtonPrimary>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default PersonnelListWithFilterDetails;
